import {format} from 'date-fns';
import {Avatar, Attributes, Separator, Flag, ColorSchemeType} from 'platform/components';
import {Box, HStack, Heading, Icon, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {css} from 'styled-components';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {RentalResourceCalendarEvent} from '../types/RentalResourceCalendarEvent';
import {getColorPathByScheme} from '../utils/getColorPathByScheme';

interface RentalEventTooltipProps extends RequiredTestIdProps {
  event: RentalResourceCalendarEvent;
}

export function RentalEventTooltip(props: RentalEventTooltipProps) {
  const formatCurrency = useFormatCurrency();

  const colorSchemeByState = match(props.event.rentalOrder?.state)
    .with('draft', always('neutral'))
    .with('reserved', always('neutral'))
    .with('rented', always('neutral'))
    .with('canceled', always('red'))
    .with('closed', always('green'))
    .otherwise(always('purple'));

  return (
    <Box padding={4}>
      <Box
        backgroundColor="palettes.white.10.100"
        borderRadius="medium"
        padding={4}
        boxShadow="elevation_4"
        minWidth={200}
        width="100%"
        border="1px solid"
        borderColor="palettes.neutral.40.100"
        data-testid={suffixTestId('tooltip', props)}
      >
        <VStack spacing={4}>
          <HStack spacing={2} align="center">
            <Heading size={3}>
              {`${props.event.rentalOrder?.number || EMPTY_PLACEHOLDER} - ${
                props.event.rentalOrder?.vehicleTitle || EMPTY_PLACEHOLDER
              }`}
            </Heading>
            {/* TODO: REPLACE WITH RENTAL ORDER STATE LABEL<Flag
              label={props.event.rentalOrder?.stateLabel || ''}
              colorScheme={
                (props.event.rentalOrder?.stateFlagColor as ColorSchemeType) || 'neutral'
              }
            /> */}
          </HStack>

          <HStack spacing={3} justify="space-between">
            <Box flex={1}>
              <HStack spacing={4} align="center">
                <Avatar icon="navigation/arrow_upward" variant="square" />
                <VStack>
                  <HStack spacing={2} align="center">
                    <Heading size={4}>{format(props.event.start, 'd.M.yyyy H:mm')}</Heading>
                    <Flag
                      size="small"
                      label={match(props.event.rentalOrder?.state)
                        .with('closed', always(i18n.t('page.rentalOrder.labels.closed')))
                        .otherwise(
                          always(props.event.rentalOrder?.stateLabel || EMPTY_PLACEHOLDER)
                        )}
                      colorScheme={
                        (props.event.rentalOrder?.stateFlagColor as ColorSchemeType) || 'neutral'
                      }
                    />
                  </HStack>
                  <Text size="xSmall" color="secondary">
                    {props.event.rentalOrder?.address || EMPTY_PLACEHOLDER}
                  </Text>
                </VStack>
              </HStack>
            </Box>
            <Box flex={1}>
              <HStack spacing={4} align="center">
                <Avatar icon="navigation/arrow_downward" variant="square" />
                <VStack>
                  <HStack spacing={2} align="center">
                    <Heading size={4}>{format(props.event.end, 'd.M.yyyy H:mm')}</Heading>
                    <Flag
                      size="small"
                      label={match(props.event.rentalOrder?.state)
                        .with('draft', always(i18n.t('page.rentalOrder.labels.scheduled')))
                        .with('reserved', always(i18n.t('page.rentalOrder.labels.scheduled')))
                        .with('rented', always(i18n.t('page.rentalOrder.labels.scheduled')))
                        .with('canceled', always(i18n.t('page.rentalOrder.labels.cancelled')))
                        .with('closed', always(i18n.t('page.rentalOrder.labels.closed')))
                        .otherwise(always(i18n.t('page.rentalOrder.labels.returned')))}
                      colorScheme={colorSchemeByState as ColorSchemeType}
                    />
                  </HStack>
                  <Text size="xSmall" color="secondary">
                    {props.event.rentalOrder?.returnAddress || EMPTY_PLACEHOLDER}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          </HStack>

          <Separator spacing={0} />

          <HStack spacing={3}>
            <Box flex={1}>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 7px;
                `}
              >
                <VStack spacing={4}>
                  <HStack spacing={1} align="center">
                    <Icon
                      size={4}
                      value="sidebar/vehicles"
                      color={getColorPathByScheme(colorSchemeByState)}
                    />
                    <Heading size={5}>{i18n.t('entity.vehicle.labels.vehicle')}</Heading>
                  </HStack>

                  <Attributes
                    size="third"
                    rows={[
                      {
                        label: i18n.t('entity.vehicle.labels.title'),
                        value: props.event.rentalOrder?.vehicleTitle || EMPTY_PLACEHOLDER,
                      },
                      {
                        label: i18n.t('entity.vehicle.labels.licensePlate'),
                        value: props.event.rentalOrder?.licensePlate || EMPTY_PLACEHOLDER,
                      },
                      {
                        label: i18n.t('entity.vehicle.labels.vin'),
                        value: props.event.rentalOrder?.vin || EMPTY_PLACEHOLDER,
                      },
                    ]}
                  />
                </VStack>

                <HStack spacing={1} align="center">
                  <Icon
                    size={4}
                    value="sidebar/customers"
                    color={getColorPathByScheme(colorSchemeByState)}
                  />
                  <Heading size={5}>{i18n.t('entity.customer.labels.customer')}</Heading>
                </HStack>

                <Attributes
                  size="third"
                  rows={[
                    {
                      label: i18n.t('general.labels.name'),
                      value: props.event.rentalOrder?.customerName || EMPTY_PLACEHOLDER,
                    },
                  ]}
                />
              </div>
            </Box>
            <Box flex={1}>
              <VStack spacing={4}>
                <HStack spacing={1} align="center">
                  <Icon
                    size={4}
                    value="action/info"
                    color={getColorPathByScheme(colorSchemeByState)}
                  />
                  <Heading size={5}>{i18n.t('general.labels.basicInformation')}</Heading>
                </HStack>

                <Attributes
                  size="third"
                  rows={[
                    {
                      label: i18n.t('page.rentalOrder.labels.dailyRate'),
                      value: props.event.rentalOrder?.dailyRate
                        ? formatCurrency(
                            props.event.rentalOrder.dailyRate.amount,
                            props.event.rentalOrder.dailyRate.currency
                          )
                        : EMPTY_PLACEHOLDER,
                    },
                    {
                      label: i18n.t('entity.vehicle.costs.expectedPriceWithoutVAT'),
                      value: props.event.rentalOrder?.expectedPrice
                        ? formatCurrency(
                            props.event.rentalOrder.expectedPrice.amount,
                            props.event.rentalOrder.expectedPrice.currency
                          )
                        : EMPTY_PLACEHOLDER,
                    },
                    {
                      label: i18n.t('entity.vehicle.costs.expectedPriceWithVAT'),
                      value: props.event.rentalOrder?.expectedPriceWithVat
                        ? formatCurrency(
                            props.event.rentalOrder.expectedPriceWithVat.amount,
                            props.event.rentalOrder.expectedPriceWithVat.currency
                          )
                        : EMPTY_PLACEHOLDER,
                    },
                    {
                      label: i18n.t('page.rentalOrder.labels.salePriceWithoutVAT'),
                      value: props.event.rentalOrder?.salePrice
                        ? formatCurrency(
                            props.event.rentalOrder.salePrice.amount,
                            props.event.rentalOrder.salePrice.currency
                          )
                        : EMPTY_PLACEHOLDER,
                    },
                    {
                      label: i18n.t('page.rentalOrder.labels.salePriceWithVAT'),
                      value: props.event.rentalOrder?.salePriceWithVat
                        ? formatCurrency(
                            props.event.rentalOrder.salePriceWithVat.amount,
                            props.event.rentalOrder.salePriceWithVat.currency
                          )
                        : EMPTY_PLACEHOLDER,
                    },
                    {
                      label: `${i18n.t('page.rentalOrder.labels.rent')} ${i18n.t('page.rentalOrder.labels.actualMileage')}`,
                      value: props.event.rentalOrder?.rentMileage
                        ? `${props.event.rentalOrder.rentMileage} ${i18n.t('general.metric.km')}`
                        : EMPTY_PLACEHOLDER,
                    },
                    {
                      label: `${i18n.t('page.rentalOrder.labels.return')} ${i18n.t('page.rentalOrder.labels.actualMileage')}`,
                      value: props.event.rentalOrder?.returnMileage
                        ? `${props.event.rentalOrder.returnMileage} ${i18n.t('general.metric.km')}`
                        : EMPTY_PLACEHOLDER,
                    },
                  ]}
                />
              </VStack>
            </Box>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
}
